import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './DataTable';
import { theme } from '../theme';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Nombre' },
  { key: 'description', label: 'Descripción' },
  { key: 'contact_count', label: 'Contactos' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
};

const overlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem',
};

function ContactLists({ token }) {
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const [successMsg, setSuccessMsg] = useState('');
  const authH = { Authorization: `Bearer ${token}` };

  // --- Modal crear / editar lista ---
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear; objeto = editar
  const [form, setForm] = useState({ name: '', description: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (list) => {
    setEditing(list);
    setForm({ name: list.name || '', description: list.description || '' });
    setFormError('');
    setFormOpen(true);
  };

  const handleFormSubmit = async () => {
    if (!form.name.trim()) { setFormError('El nombre es requerido'); return; }
    setFormError('');
    setFormLoading(true);
    try {
      const url = editing ? `/api/contact-lists/${editing.id}` : '/api/contact-lists';
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify({ name: form.name.trim(), description: form.description.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Error al guardar'); return; }
      setFormOpen(false);
      showSuccess(editing ? 'Lista actualizada.' : 'Lista creada.');
      tableRef.current?.reload();
    } catch {
      setFormError('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (list) => {
    if (!window.confirm(`¿Eliminar la lista "${list.name}"? Los contactos no se borran, solo se desvinculan.`)) return;
    try {
      const res = await fetch(`/api/contact-lists/${list.id}`, { method: 'DELETE', headers: authH });
      const data = await res.json();
      if (!res.ok) { showSuccess(data.message || 'Error al eliminar'); return; }
      showSuccess('Lista eliminada.');
      tableRef.current?.reload();
    } catch {
      showSuccess('Error de conexión');
    }
  };

  const handleExport = async (list) => {
    try {
      const res = await fetch(`/api/contact-lists/${list.id}/export`, { headers: authH });
      if (!res.ok) { showSuccess('Error al exportar'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contactos_${(list.name || 'lista').replace(/[^\w-]+/g, '_')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showSuccess('Error de conexión');
    }
  };

  const actionBtn = (color) => ({
    padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${color}`,
    borderRadius: '6px', color, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  });

  return (
    <main style={{ padding: '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>Listas de contactos</h1>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>Agrupa contactos en listas para usarlas en flujos y campañas.</p>
          </div>
          <button
            onClick={openCreate}
            style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
          >
            + Crear
          </button>
        </div>

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.colors.success, fontSize: '0.9rem' }}>
            {successMsg}
          </div>
        )}

        <DataTable
          ref={tableRef}
          columns={COLUMNS}
          fetchUrl="/api/contact-lists"
          token={token}
          renderActions={(row) => (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={() => navigate(`/listas-contactos/${row.id}`)} style={actionBtn(theme.colors.primary)}>Ver</button>
              <button onClick={() => openEdit(row)} style={actionBtn(theme.colors.primary)}>Editar</button>
              <button onClick={() => handleExport(row)} style={actionBtn(theme.colors.success)}>Exportar</button>
              <button onClick={() => handleDelete(row)} style={actionBtn(theme.colors.error)}>Eliminar</button>
            </div>
          )}
        />
      </div>

      {/* Modal crear / editar lista */}
      {formOpen && (
        <div onClick={() => !formLoading && setFormOpen(false)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '420px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>{editing ? `Editar lista #${editing.id}` : 'Nueva lista'}</h2>
              <button onClick={() => !formLoading && setFormOpen(false)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Nombre</label>
              <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} maxLength={100} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Descripción <span style={{ color: theme.colors.textMuted }}>(opcional)</span></label>
              <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} />
            </div>

            {formError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{formError}</p>}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setFormOpen(false)} disabled={formLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleFormSubmit} disabled={formLoading || !form.name.trim()} style={{ flex: 1, padding: '10px', backgroundColor: formLoading || !form.name.trim() ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: formLoading || !form.name.trim() ? 'not-allowed' : 'pointer' }}>{formLoading ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ContactLists;
